import { useState, useEffect } from 'react';
import { Box, Card, CardContent, Divider, IconButton, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useLocation } from 'react-router-dom';
import Header from '@/component/Header';
import RewardPeriodForm from '@/component/stake-db/reward/RewardPeriodForm';
import { Tables } from '@/component/pools/table/Tables';
import { readRewardPeriods, readRewardPositions } from '@/api/stake-db';
import { RewardPeriod, RewardPosition, ReadRewardPositionsResponse } from '@/types/stake-db';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

const RewardPeriodColumns: GridColDef[] = [
  {
    minWidth: 280,
    field: 'rewardPeriodId',
    headerName: 'Reward Period ID',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
  {
    minWidth: 180,
    field: 'startAt',
    headerName: 'Start At',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    valueFormatter: (params) => params.value || '-',
  },
  {
    minWidth: 180,
    field: 'endAt',
    headerName: 'End At',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    valueFormatter: (params) => params.value || '-',
  },
  {
    minWidth: 100,
    field: 'state',
    headerName: 'State',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
  {
    minWidth: 100,
    field: 'ticker',
    headerName: 'Ticker',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
  {
    minWidth: 150,
    field: 'totalExpectedAmount',
    headerName: 'Expected Amount',
    headerAlign: 'center',
    align: 'right',
    sortable: false,
    valueFormatter: (params) => params.value || '-',
  },
  {
    minWidth: 150,
    field: 'totalExpectedUsdt',
    headerName: 'Expected USDT',
    headerAlign: 'center',
    align: 'right',
    sortable: false,
    valueFormatter: (params) => params.value || '-',
  },
];

const RewardPositionColumns: GridColDef[] = [
  {
    minWidth: 100,
    field: 'stakingDays',
    headerName: 'Staking Days',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
  {
    minWidth: 350,
    field: 'walletAddress',
    headerName: 'Wallet Address',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
  {
    minWidth: 150,
    field: 'netStakeAmount',
    headerName: 'Net Stake Amount',
    headerAlign: 'center',
    align: 'right',
    sortable: false,
  },
  {
    minWidth: 150,
    field: 'netUsdtValue',
    headerName: 'Net USDT Value',
    headerAlign: 'center',
    align: 'right',
    sortable: false,
  },
  {
    minWidth: 130,
    field: 'stakeTicker',
    headerName: 'Stake Ticker',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
  },
  {
    minWidth: 150,
    field: 'rewardAmount',
    headerName: 'Reward Amount',
    headerAlign: 'center',
    align: 'right',
    sortable: false,
  },
  {
    minWidth: 150,
    field: 'rewardUsdt',
    headerName: 'Reward USDT',
    headerAlign: 'center',
    align: 'right',
    sortable: false,
  },
  {
    minWidth: 180,
    field: 'rewardedAt',
    headerName: 'Rewarded At',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    valueFormatter: (params) => params.value || '-',
  },
  {
    minWidth: 80,
    field: 'txHash',
    headerName: 'Tx',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams) => {
      if (!params.value) return '-';
      return (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            window.open(`https://polygonscan.com/tx/${params.value}`, '_blank');
          }}
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      );
    },
  },
];

export default function StakeDbRewardPeriod() {
  const location = useLocation();
  const poolId = location.state?.poolId || '';
  const [rewardPeriods, setRewardPeriods] = useState<RewardPeriod[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [positions, setPositions] = useState<(RewardPosition & { stakingDays: number })[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);

  const fetchRewardPeriods = async () => {
    if (!poolId) return;

    setLoading(true);
    try {
      const re = await readRewardPeriods(poolId);
      if (re.status === 200) {
        setRewardPeriods(re.data.rewardPeriods || []);
      }
    } catch (error) {
      console.error('Failed to fetch reward periods:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRewardPeriods();
  }, [poolId]);

  const handleSuccess = () => {
    fetchRewardPeriods();
  };

  const handleRowSelect = async (row: RewardPeriod) => {
    const rewardPeriodId = row.rewardPeriodId;
    if (selectedPeriodId === rewardPeriodId) {
      setSelectedPeriodId('');
      setPositions([]);
      return;
    }

    setSelectedPeriodId(rewardPeriodId);
    setPositionsLoading(true);
    try {
      const re = await readRewardPositions(rewardPeriodId);
      if (re.status === 200) {
        const data: ReadRewardPositionsResponse = re.data;
        const flatPositions = (data.rewards || []).map((reward) => ({
          ...reward,
          stakingDays: data.stakingDays,
        }));
        setPositions(flatPositions);
      }
    } catch (error) {
      console.error('Failed to fetch reward positions:', error);
    } finally {
      setPositionsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'auto',
        flex: 1,
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Card>
        <Header title={`Stake-db Reward Period - ${poolId}`} />
        <Divider />
        <CardContent>
          <RewardPeriodForm poolId={poolId} onSuccess={handleSuccess} existingPeriods={rewardPeriods} />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Reward Period List
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {!loading && (
            <Tables
              list={rewardPeriods}
              columns={RewardPeriodColumns}
              getRowId={(row) => row.rewardPeriodId}
              isToolbar={false}
              onRowSelect={handleRowSelect}
            />
          )}
        </CardContent>
      </Card>

      {selectedPeriodId && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Reward Positions - {selectedPeriodId}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {positionsLoading ? (
              <Typography>Loading...</Typography>
            ) : (
              <Tables
                list={positions}
                columns={RewardPositionColumns}
                getRowId={(row) => row.rewardId}
                isToolbar={false}
              />
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
